import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import Modal from '../../controls/Modal';
import LabeledInput from '../../controls/LabeledInput';
import LabeledSelect from '../../controls/LabeledSelect';
import SpiteToggle from './SpiteToggle';
import ImageUpload from './ImageUpload';

const LIMIT_OPTIONS = [
    { key: 'UNLIMITED', value: 'Без ограничений' },
    { key: 'LIMIT', value: 'Лимит на количество' }
];

// Модалка создания/редактирования шаблона задания.
export default function TemplateModal({ template, categoryId, subcategories, defaultSubcategoryId, onSuccess, onClose }) {
    const editing = !!template;
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [file, setFile] = useState(null);
    const [form, setForm] = useState({
        subcategoryId: template?.subcategoryId || defaultSubcategoryId || subcategories[0]?.id || '',
        title: template?.title || '',
        description: template?.description || '',
        conditions: template?.conditions || '',
        cost: template?.cost ?? 10,
        withTargetUser: template?.withTargetUser || false,
        requiresBothConfirmation: template?.requiresBothConfirmation || false,
        isChallenge: template?.isChallenge || false,
        useTime: template?.useTime || false,
        involvedInDaily: template?.involvedInDaily || false,
        availableCountType: template?.availableCountType || 'UNLIMITED',
        availableCount: template?.availableCount ?? null
    });

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
    const toggle = (field) => () => set(field, !form[field]);

    const submit = async () => {
        if (!form.title.trim()) { enqueueSnackbar('Введите название шаблона', { variant: 'warning' }); return; }
        if (!form.subcategoryId) { enqueueSnackbar('Выберите подкатегорию', { variant: 'warning' }); return; }
        const { availableCountType, ...rest } = form;
        const requestData = {
            ...(editing ? {} : { categoryId }),
            ...rest,
            availableCountType,
            availableCount: availableCountType !== 'UNLIMITED' ? form.availableCount : null
        };
        const fd = new FormData();
        fd.append('request', new Blob([JSON.stringify(requestData)], { type: 'application/json' }));
        if (file) fd.append('file', file);
        show();
        try {
            if (editing) {
                await apiClient.patch(`/api/neighbor/admin/task-templates/update?id=${template.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else {
                await apiClient.post('/api/neighbor/admin/task-templates/create', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            enqueueSnackbar(editing ? 'Шаблон обновлён' : 'Шаблон создан', { variant: 'success' });
            onSuccess();
            onClose();
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при сохранении', { variant: 'error' }); }
        finally { hide(); }
    };

    const del = async () => {
        show();
        try {
            await apiClient.delete(`/api/neighbor/admin/task-templates/delete?id=${template.id}`);
            enqueueSnackbar('Шаблон удалён', { variant: 'success' });
            onSuccess();
            onClose();
        } catch (e) { enqueueSnackbar('Ошибка при удалении', { variant: 'error' }); }
        finally { hide(); }
    };

    const subOptions = subcategories.map(s => ({ key: s.id, value: s.name }));

    const footer = (
        <>
            {editing && <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={del}>Удалить</button>}
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="button" className="btn btn-primary" onClick={submit}>{editing ? 'Сохранить' : 'Создать'}</button>
        </>
    );

    return (
        <Modal open kicker="Шаблон" title={editing ? 'Редактирование шаблона' : 'Создание шаблона'} size="lg" onClose={onClose} footer={footer}>
            <LabeledInput label="Название" value={form.title} onChange={(e) => set('title', e.target.value)} />
            <div>
                <label className="ctl-label">Описание</label>
                <textarea className="textarea" value={form.description} onChange={(e) => set('description', e.target.value)} />
            </div>
            <div>
                <label className="ctl-label">Особые условия</label>
                <textarea className="textarea" value={form.conditions} onChange={(e) => set('conditions', e.target.value)} />
            </div>
            <div className="fr-grid2">
                <LabeledSelect label="Подкатегория" value={form.subcategoryId} onChange={(e) => set('subcategoryId', e.target.value)} options={subOptions} placeholder="Выберите подкатегорию" />
                <LabeledInput label="Стоимость" type="number" value={String(form.cost)} onChange={(e) => set('cost', parseInt(e.target.value, 10) || 0)} />
            </div>
            <div className="fr-grid2">
                <LabeledSelect label="Тип доступности" value={form.availableCountType} onChange={(e) => set('availableCountType', e.target.value)} options={LIMIT_OPTIONS} />
                {form.availableCountType !== 'UNLIMITED' && (
                    <LabeledInput label="Доступное количество" type="number" value={form.availableCount == null ? '' : String(form.availableCount)} onChange={(e) => set('availableCount', e.target.value === '' ? null : parseInt(e.target.value, 10))} />
                )}
            </div>

            <div className="sp-sec">
                <h4>Настройки</h4>
                <div className="sp-tgs">
                    <SpiteToggle label="Назначаемое задание" checked={form.withTargetUser} onChange={toggle('withTargetUser')} />
                    <SpiteToggle label="Подтверждение обоих" checked={form.requiresBothConfirmation} onChange={toggle('requiresBothConfirmation')} />
                    <SpiteToggle label="Используется в рулетке" checked={form.involvedInDaily} onChange={toggle('involvedInDaily')} />
                    <SpiteToggle label="Ограниченное время" checked={form.useTime} onChange={toggle('useTime')} full />
                </div>
            </div>

            <div className="sp-sec">
                <h4>Изображение</h4>
                <ImageUpload existingUrl={template?.mediaFile?.mediaFileUrl || null} pendingFile={file} onFileSelect={setFile} onClear={() => setFile(null)} />
            </div>
        </Modal>
    );
}

TemplateModal.propTypes = {
    template: PropTypes.object,
    categoryId: PropTypes.number,
    subcategories: PropTypes.array.isRequired,
    defaultSubcategoryId: PropTypes.number,
    onSuccess: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

TemplateModal.defaultProps = { template: null, categoryId: null, defaultSubcategoryId: null };
